import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { WAITLIST_ONLY } from '../config';

const AuthContext = createContext();

const initialState = {
  isAuthenticated: false,
  user: null,
  businesses: [],
  currentBusiness: null,
  loading: false,
  error: null,
  waitlistJoined: false,
  waitlistEmail: null,
  authenticatedProviders: {
    google: false,
    facebook: false,
    instagram: false,
  },
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'AUTH_START':
      return { ...state, loading: true, error: null };

    case 'AUTH_SUCCESS':
      // Hard block: never grant product auth while waitlist-only
      if (WAITLIST_ONLY) {
        return {
          ...initialState,
          error: null,
          waitlistJoined: state.waitlistJoined,
          waitlistEmail: state.waitlistEmail,
        };
      }
      return {
        ...state,
        loading: false,
        isAuthenticated: true,
        businesses: action.payload.businesses,
        currentBusiness: action.payload.businesses[0] || null,
        authenticatedProviders: action.payload.providers,
      };

    case 'AUTH_FAILURE':
      return { ...state, loading: false, error: action.payload };

    case 'WAITLIST_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        isAuthenticated: false,
        businesses: [],
        currentBusiness: null,
        waitlistJoined: true,
        waitlistEmail: action.payload.email,
        authenticatedProviders: {
          google: false,
          facebook: false,
          instagram: false,
        },
      };

    case 'SET_CURRENT_BUSINESS':
      if (WAITLIST_ONLY) return state;
      return { ...state, currentBusiness: action.payload };

    case 'UPDATE_BUSINESSES':
      if (WAITLIST_ONLY) return state;
      return { ...state, businesses: action.payload };

    case 'LOGOUT':
      return {
        ...initialState,
        waitlistJoined: state.waitlistJoined,
        waitlistEmail: state.waitlistEmail,
      };

    case 'UPDATE_PROVIDERS':
      if (WAITLIST_ONLY) {
        return {
          ...state,
          authenticatedProviders: {
            google: false,
            facebook: false,
            instagram: false,
          },
        };
      }
      return { ...state, authenticatedProviders: action.payload };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(() => {
    // Always scrub leftover demo/OAuth tokens so stale localStorage cannot unlock UI
    authService.clearAllAuthStorage();
    if (WAITLIST_ONLY) {
      dispatch({ type: 'LOGOUT' });
      return;
    }
    checkExistingAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkExistingAuth = async () => {
    if (WAITLIST_ONLY) {
      authService.clearAllAuthStorage();
      dispatch({ type: 'LOGOUT' });
      return;
    }
    try {
      const providers = authService.getAuthenticatedProviders();
      dispatch({ type: 'UPDATE_PROVIDERS', payload: providers });

      const hasAnyAuth = Object.values(providers).some(Boolean);
      if (hasAnyAuth) {
        const businesses = authService.getCurrentBusinessData();
        if (businesses.length > 0) {
          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { businesses, providers },
          });
        }
      }
    } catch (error) {
      console.error('Error checking existing auth:', error);
    }
  };

  /**
   * Waitlist join only — stores email, never sets isAuthenticated / tokens.
   */
  const joinWaitlist = useCallback(async (email) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const result = await authService.joinWaitlist(email);
      dispatch({
        type: 'WAITLIST_SUCCESS',
        payload: { email: result.email },
      });
      return result;
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message });
      throw error;
    }
  }, []);

  /**
   * Product login is disabled in waitlist mode.
   * Callers that still pass `login` get waitlist behavior for email, hard fail otherwise.
   */
  const login = async (provider, email = null) => {
    if (WAITLIST_ONLY) {
      const p = String(provider || '').toLowerCase();
      if ((p === 'email' || p === 'waitlist') && email) {
        return joinWaitlist(email);
      }
      dispatch({
        type: 'AUTH_FAILURE',
        payload: 'Login is disabled. Join the waitlist for early access.',
      });
      throw new Error('Login is disabled. Join the waitlist for early access.');
    }

    try {
      dispatch({ type: 'AUTH_START' });
      const result = await authService.initiateAuth(provider, email);

      if (result && Array.isArray(result)) {
        const providers = authService.getAuthenticatedProviders();
        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { businesses: result, providers },
        });
      }
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message });
    }
  };

  const handleAuthCallback = async (provider, code) => {
    if (WAITLIST_ONLY) {
      authService.clearAllAuthStorage();
      throw new Error('Login is disabled. Join the waitlist for early access.');
    }
    try {
      dispatch({ type: 'AUTH_START' });
      const businesses = await authService.handleAuthCallback(provider, code);
      const providers = authService.getAuthenticatedProviders();
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { businesses, providers },
      });
      return businesses;
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message });
      throw error;
    }
  };

  const logout = async (provider = 'all') => {
    try {
      await authService.logout(provider);
      authService.clearAllAuthStorage();
      dispatch({ type: 'LOGOUT' });
    } catch (error) {
      console.error('Logout error:', error);
      authService.clearAllAuthStorage();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const setCurrentBusiness = (business) => {
    if (WAITLIST_ONLY) return;
    dispatch({ type: 'SET_CURRENT_BUSINESS', payload: business });
  };

  const refreshBusinessData = async () => {
    if (WAITLIST_ONLY) {
      throw new Error('Product access is not available while on the waitlist.');
    }
    try {
      dispatch({ type: 'AUTH_START' });
      const providers = authService.getAuthenticatedProviders();
      let allBusinesses = [];

      if (providers.google) {
        try {
          const googleBusinesses = await authService.loadGoogleBusinessData();
          allBusinesses = [...allBusinesses, ...googleBusinesses];
        } catch (error) {
          console.error('Failed to refresh Google business data:', error);
        }
      }

      if (providers.facebook) {
        try {
          const facebookBusinesses = await authService.loadFacebookBusinessData();
          allBusinesses = [...allBusinesses, ...facebookBusinesses];
        } catch (error) {
          console.error('Failed to refresh Facebook business data:', error);
        }
      }

      dispatch({ type: 'UPDATE_BUSINESSES', payload: allBusinesses });
      return allBusinesses;
    } catch (error) {
      dispatch({ type: 'AUTH_FAILURE', payload: error.message });
      throw error;
    }
  };

  const getBusinessInsights = async (businessId, provider) => {
    if (WAITLIST_ONLY) return [];
    try {
      return await authService.getBusinessInsights(businessId, provider);
    } catch (error) {
      console.error('Error fetching business insights:', error);
      return [];
    }
  };

  // Public surface always reports logged-out while waitlist-only
  const value = {
    ...state,
    isAuthenticated: WAITLIST_ONLY ? false : state.isAuthenticated,
    login,
    joinWaitlist,
    handleAuthCallback,
    logout,
    setCurrentBusiness,
    refreshBusinessData,
    getBusinessInsights,
    waitlistOnly: WAITLIST_ONLY,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
